import React from 'react';
import { useNavigate } from 'react-router-dom';

import styled from 'styled-components';

import SorterIcon from 'common/Sort/SorterIcon';
import { StyledTable } from 'common/Table/StyledTable';
import { ACTION_PLAN_STATUS } from 'components/ActionPlans/constants';
import FocusAreaTag from 'components/ActionPlans/FocusAreaTag';
import DetailsButton from 'components/lib/DetailsButton';
import { ActionPlanCompanyOverallRowType } from 'types/actionPlans';

import ActionPlansCompanyProgressBar from './ActionPlansCompanyProgressBar';
import ActionPlansCompanyStatusCell from './ActionPlansCompanyStatusCell';
import ActionPlansCompanyTableWrapper from './ActionPlansCompanyTableWrapper';
import { SORT_ORDER } from '../../../constants';

const getColumnSortOrder = (ordering: string | undefined, field: string) => {
  if (ordering === field) {
    return SORT_ORDER.ASCEND;
  }
  if (ordering === `-${field}`) {
    return SORT_ORDER.DESCEND;
  }
  return null;
};

const getOrderingFromSorter = (sorter: any): string | undefined => {
  if (!sorter?.order || !sorter?.columnKey) {
    return undefined;
  }
  return sorter.order === SORT_ORDER.DESCEND ? `-${sorter.columnKey}` : sorter.columnKey;
};

const OverallStyledTable = styled(StyledTable)`
  &&& {
    th.ant-table-column-sort,
    td.ant-table-column-sort {
      background: transparent;
    }
  }
`;

const CommunityName = styled.div`
  font-size: 14px;
  font-family: var(--font-bold);
  color: var(--text-primary);
`;

const DetailsCellContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

type Props = {
  rows: ActionPlanCompanyOverallRowType[];
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  ordering?: string;
  onOrderingChange: (ordering: string | undefined) => void;
};

const ActionPlansCompanyOverallTable = ({
  rows,
  page,
  pageSize,
  total,
  onPageChange,
  ordering,
  onOrderingChange,
}: Props) => {
  const navigate = useNavigate();

  const columns = [
    {
      title: 'COMMUNITY',
      dataIndex: 'community_name',
      key: 'community_name',
      width: 310,
      sorter: true,
      sortOrder: getColumnSortOrder(ordering, 'community_name'),
      sortIcon: ({ sortOrder }: any) => <SorterIcon sortOrder={sortOrder} />,
      render: (name: string) => (
        <CommunityName data-test-id="action-plans-company-overall-row-community-name">{name}</CommunityName>
      ),
    },
    {
      title: 'ACTIVE PLANS',
      dataIndex: 'active_plans',
      key: 'active_plans',
      width: 120,
      sorter: true,
      sortOrder: getColumnSortOrder(ordering, 'active_plans'),
      sortIcon: ({ sortOrder }: any) => <SorterIcon sortOrder={sortOrder} />,
      render: (activePlans: number) => (
        <span data-test-id="action-plans-company-overall-row-active-plans">{activePlans}</span>
      ),
    },
    {
      title: 'COMPLETION RATE',
      dataIndex: 'completion_rate',
      key: 'completion_rate',
      width: 160,
      render: (percent: number) => (
        <ActionPlansCompanyProgressBar
          percent={percent}
          dataTestId="action-plans-company-overall-row-completion-rate"
        />
      ),
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      width: 150,
      sorter: true,
      sortOrder: getColumnSortOrder(ordering, 'status'),
      sortIcon: ({ sortOrder }: any) => <SorterIcon sortOrder={sortOrder} />,
      render: (status: number, row: ActionPlanCompanyOverallRowType) => (
        <ActionPlansCompanyStatusCell
          status={status}
          overdueLabel={
            status !== ACTION_PLAN_STATUS.COMPLETE && row.overdue_count > 0
              ? `Overdue (${row.overdue_count})`
              : undefined
          }
          dataTestId="action-plans-company-overall-row-status"
        />
      ),
    },
    {
      title: 'MOST COMMON FOCUS AREA',
      dataIndex: 'most_common_focus_area',
      key: 'most_common_focus_area',
      width: 220,
      render: (focusArea: string | null) => (
        <span data-test-id="action-plans-company-overall-row-focus-area">
          {focusArea ? <FocusAreaTag label={focusArea} /> : '-'}
        </span>
      ),
    },
    {
      title: '',
      key: 'details',
      width: 90,
      render: (_: unknown, row: ActionPlanCompanyOverallRowType) => (
        <DetailsCellContainer className="actions-hover-reveal">
          <DetailsButton
            data-test-id="action-plans-company-overall-row-details"
            onClick={() => navigate(`/community/${row.community_id}/action-plans`)}
          />
        </DetailsCellContainer>
      ),
    },
  ];

  return (
    <ActionPlansCompanyTableWrapper data-test-id="action-plans-company-overall-table">
      <OverallStyledTable
        columns={columns as any}
        dataSource={rows}
        rowKey={((row: ActionPlanCompanyOverallRowType) => row.community_id) as any}
        locale={{
          emptyText: <span data-test-id="action-plans-company-overall-table-empty">No data</span>,
        }}
        onChange={(_pagination: any, _filters: any, sorter: any) => {
          const nextOrdering = getOrderingFromSorter(sorter);
          if (nextOrdering !== ordering) {
            onOrderingChange(nextOrdering);
          }
        }}
        showSorterTooltip={false}
        pagination={{
          size: 'small',
          position: ['bottomLeft'],
          showSizeChanger: false,
          current: page,
          pageSize,
          total,
          onChange: (pageNumber: number) => onPageChange(pageNumber),
          showTotal: (totalCount, range) => (
            <span>
              Showing {range[1] - range[0] + 1} of {totalCount} records
            </span>
          ),
        }}
        size="middle"
        bordered={false}
        tableLayout="fixed"
      />
    </ActionPlansCompanyTableWrapper>
  );
};

export default ActionPlansCompanyOverallTable;
